import React from 'react';
import {
  ZfAccordion,
  ZfBox,
  ZfDrawerHeader,
  ZfDrawerFooter,
  ZfStack,
  ZfTextButton,
  ZfTypography,
  ZfAccordionItem,
} from '@zafire/react';
import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { openModal } from '../../reducers/modalReducer';
import { PortfolioTableItemDetails } from './PortfolioTableItem';
import { StatusPill } from 'views/PrivateBanking/StatusPill';
import { DetailsSection } from './PortfolioSections/DetailsSection';
import { AdditionalDetailsSection } from './PortfolioSections/AdditionalDetailsSection';
import { clearSelectedPortfolio } from '../../reducers/portfolioReducer';
import { RevisionHistory } from './PortfolioSections/RevisionHistory';

type PortfolioDetailsProps = {
  portfolio: PortfolioTableItemDetails;
};

const PortfolioDetails: React.FC<PortfolioDetailsProps> = ({ portfolio }) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { brUid } = useParams();
  const { t } = useTranslation();
  const onClose = () => dispatch(clearSelectedPortfolio());
  const onEdit = () =>
    dispatch(
      openModal({
        modalType: 'AddPortfolioDialog',
        modalProps: {
          brUid,
          portfolio,
          onProceed: () => queryClient.invalidateQueries({ queryKey: ['useGetPortfoliosList', brUid] }),
        },
      }),
    );
  // const blocks = getProductDetailsBlocks(product);

  return (
    <ZfBox style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ZfDrawerHeader onClick={onClose}>Portfolio Details</ZfDrawerHeader>

      <ZfStack
        id="private-banking-details-container"
        spacing="medium"
        style={{
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflowY: 'auto',
        }}
      >
        <ZfStack direction="row" spacing="small">
          <ZfTypography variant="body-large">{portfolio.portfolioNumber ?? '-'}</ZfTypography>
          <StatusPill status={portfolio.portfolioStatus} clickable={false} />
        </ZfStack>

        <ZfAccordion type="multiple">
          <ZfAccordionItem state="active" titleText="Details">
            <DetailsSection portfolio={portfolio} />
          </ZfAccordionItem>
          <ZfAccordionItem titleText="Additional details">
            <AdditionalDetailsSection portfolio={portfolio} />
          </ZfAccordionItem>
          <ZfAccordionItem state="active" titleText="Portfolio revision history">
            <RevisionHistory portfolio={portfolio} />
          </ZfAccordionItem>
        </ZfAccordion>
      </ZfStack>

      <ZfDrawerFooter>
        {portfolio.canBeEdited && (
          <ZfTextButton
            slot="primary"
            hierarchy="secondary"
            size="small"
            data-testid="portfolio-detail-edit"
            onClick={onEdit}
          >
            {t('pb.portfoliosummary.edit')}
          </ZfTextButton>
        )}
        <ZfTextButton slot="primary" hierarchy="tertiary" size="small" onClick={onClose}>
          Cancel
        </ZfTextButton>
      </ZfDrawerFooter>
    </ZfBox>
  );
};

export default PortfolioDetails;
